import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { API_ROUTES } from '@mangelapp/shared';

export interface NotificationItem {
  id: string;
  userId: string;
  organizationId?: string | null;
  projectId?: string | null;
  ticketId?: string | null;
  type: string;
  category: string;
  title: string;
  message: string;
  data?: Record<string, any> | null;
  priority: string;
  actionUrl?: string | null;
  iconUrl?: string | null;
  createdAt: string;
  isRead: boolean;
}

export interface NotificationsListResponse {
  success: boolean;
  data: NotificationItem[];
}

export interface UnreadCountResponse {
  success: boolean;
  data: { count: number };
}

const NOTIFICATIONS_QUERY_KEY = ['notifications'];
const UNREAD_COUNT_QUERY_KEY = ['notifications', 'unreadCount'];

export function useNotifications(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, { limit, offset }],
    queryFn: async () => {
      const response = await apiClient.get<NotificationsListResponse>(
        `${API_ROUTES.NOTIFICATIONS.LIST}?limit=${limit}&offset=${offset}`
      );
      return response.data;
    },
  });
}

export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<UnreadCountResponse>(
        API_ROUTES.NOTIFICATIONS.UNREAD_COUNT
      );
      return response.data.count;
    },
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider stale after 30 seconds
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.put(API_ROUTES.NOTIFICATIONS.READ(notificationId));
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.put(API_ROUTES.NOTIFICATIONS.READ_ALL);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.delete(API_ROUTES.NOTIFICATIONS.DELETE(notificationId));
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
    },
  });
}
